import { FC, useMemo } from 'react';
import Drawer from '..';
import DrawerHeader from '../header';
import DrawerContent from '../content';
import { useQuery } from 'react-query';
import { UserEventModel } from '@/lib/models/user-event/user-event.model';
import { getUserEventById } from '@/lib/services/user-event.service';
import { QueryKey } from '@/lib/query-client';
import RouterTabs from '@/components/router-tabs';
import RequestDetails from './request-details';
import Overview from './overview';
import InlineToast from '@/components/toasts/inline';
import Tools from './tools';
import useFeature from '@/hooks/use-feature.hook';
import { UserEventAction } from '@/lib/models/user-event';
import { TabPanel } from '@/components/router-tabs/helpers';
import { DrawerProps } from '../helpers';
import EventMessages from './event-messages';
import UserEventMenu from './menu';

interface Props extends DrawerProps {
  id: string;
}

const hideConversationTab = [
  UserEventAction.PASSTHROUGH,
  UserEventAction.INTERCEPTAUTH,
  UserEventAction.PORTAL,
];

export const UserEventDrawer: FC<Props> = ({ id, open, onClose, className, isTopDrawer }) => {
  const query = useQuery([QueryKey.UserEventView, id], async () => getUserEventById(id), {
    enabled: !!id,
  });

  const { canViewConversations, getTooltip } = useFeature();

  const { data = new UserEventModel() } = query;

  const { messages, policyDecision, compTraceId, conversationId, tools } = data || {};

  const showToolsTab = tools !== undefined;

  const hasConversation = Boolean(messages?.conversationCount);
  const conversationTooltip = useMemo(() => {
    if (!canViewConversations) {
      return getTooltip('view-conversation');
    }
    if (!hasConversation) {
      return 'No conversation history';
    }
  }, [canViewConversations, hasConversation, getTooltip]);
  const decision = policyDecision?.decision;
  const showConversationTab = decision && !hideConversationTab.includes(decision);

  const tabs = useMemo(() => {
    if (!data) {
      return [];
    }

    const panels: TabPanel[] = [
      {
        label: 'Overview',
        icon: 'info-circle',
        path: 'overview',
        component: <Overview data={data} />,
      },
      {
        label: 'Request Details',
        icon: 'article',
        path: 'request-details',
        component: <RequestDetails data={data} />,
      },
    ];

    if (showToolsTab) {
      panels.push({
        label: 'Tools',
        icon: 'mcp-server',
        path: 'tools',
        component: <Tools data={data} />,
      });
    }

    if (showConversationTab) {
      panels.push({
        label: 'Messages',
        icon: 'chat',
        path: 'messages',
        disabled: !!conversationTooltip,
        tooltip: conversationTooltip,
        withToolbar: true,
        component: <EventMessages userEvent={data} />,
      });
    }

    return panels;
  }, [data, conversationTooltip, showToolsTab, showConversationTab]);

  return (
    <Drawer open={open} onClose={onClose} className={className} query={query} size="medium">
      <DrawerHeader
        onClose={onClose}
        isTopDrawer={isTopDrawer}
        menu={<UserEventMenu traceId={compTraceId} conversationId={conversationId} />}
      >
        User Event Details
      </DrawerHeader>
      <DrawerContent>
        {data ? (
          <RouterTabs variant="drawer" panels={tabs} />
        ) : (
          <InlineToast
            message="User event not found. Please try again. If the problem persists, please contact support."
            level="error"
          />
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default UserEventDrawer;
